import {
  nodeColors,
  nodeTextColor,
  affiliationComboColors,
  comboStyles,
} from 'utils/constants';

// ── Node rules ──────────────────────────────────────────────────────
const nodeRules = [
  {
    selector: 'node',
    style: {
      label: 'data(label)',
      'text-wrap': 'wrap',
      'text-max-width': 44,
      'text-valign': 'center',
      'text-halign': 'center',
      'font-size': 9,
      'font-weight': 500,
      color: nodeTextColor,
      'min-zoomed-font-size': 7,
      'background-color': '#ceced3',
      'border-width': 1,
      'border-color': '#ceced3',
      'text-background-opacity': 0,
    },
  },
  // Dynamic label width and font size proportional to node tier
  // mostConnectedSize is 1–8 (same as ReGraph tier, stored in element data)
  {
    selector: 'node[mostConnectedSize]',
    style: {
      'text-max-width': 'mapData(mostConnectedSize, 1, 8, 36, 68)',
      'font-size': 'mapData(mostConnectedSize, 1, 8, 8, 12)',
    },
  },
  // Discrete per-tier width/height. The transform no longer writes inline
  // width/height (Phase 8 / S4) — these rules are now the sole source of
  // truth for plain nodes' size.
  { selector: 'node[mostConnectedSize = 1]', style: { width: 20, height: 20 } },
  { selector: 'node[mostConnectedSize = 2]', style: { width: 28, height: 28 } },
  { selector: 'node[mostConnectedSize = 3]', style: { width: 36, height: 36 } },
  { selector: 'node[mostConnectedSize = 4]', style: { width: 46, height: 46 } },
  { selector: 'node[mostConnectedSize = 6]', style: { width: 58, height: 58 } },
  { selector: 'node[mostConnectedSize = 8]', style: { width: 74, height: 74 } },
  {
    selector: 'node[type="person"]',
    style: { 'background-color': nodeColors.person, 'border-color': nodeColors.person },
  },
  {
    selector: 'node[type="organization"]',
    style: { 'background-color': nodeColors.organization, 'border-color': nodeColors.organization },
  },
  {
    selector: 'node[type="fund"]',
    style: { 'background-color': nodeColors.fund, 'border-color': nodeColors.fund },
  },
  {
    selector: 'node[type="education"]',
    style: { 'background-color': nodeColors.education, 'border-color': nodeColors.education },
  },
  {
    selector: 'node[type="engagement"]',
    style: {
      'background-color': nodeColors.engagement,
      'border-color': nodeColors.engagement,
      'font-size': 8,
      color: '#6d6d6d',
    },
  },
  {
    selector: 'node[type="event"]',
    style: { 'background-color': nodeColors.event, 'border-color': nodeColors.event },
  },
  {
    selector: 'node[type="filter_session"]',
    style: { 'background-color': '#38b2d9', 'border-color': '#38b2d9' },
  },
  // Data-driven background colour override for nodes whose `item.color`
  // differs from the type default (context nodes, suggested nodes with
  // signal colours, filter_session hub, etc.). Declared AFTER the
  // `node[type="..."]` rules so it wins the same-specificity cascade.
  // Declared BEFORE `node[?isContext]` / `node[?multiAffiliation]` so
  // those still drive border-color for the context / multi-affiliation
  // states.
  {
    selector: 'node[bgColor]',
    style: {
      'background-color': 'data(bgColor)',
      'border-color': 'data(bgColor)',
    },
  },
  {
    selector: 'node[?isContext]',
    style: {
      'font-size': 12,
      'font-weight': 600,
      color: '#1a1a1a',
      'border-width': 4,
      'border-color': '#EC4863',
      'underlay-opacity': 0.12,
      'underlay-color': '#EC4863',
      'underlay-padding': 6,
    },
  },
  {
    selector: 'node[?multiAffiliation]',
    style: { 'border-color': comboStyles.multiAffiliationBorder, 'border-width': 3 },
  },
  // Per-item border width (item.border.width: e.g. 5 for context nodes,
  // 3 for suggested). Declared AFTER `node[?isContext]` and
  // `node[?multiAffiliation]` so the per-item value wins over the class
  // defaults; declared BEFORE the sparse rules so sparse can still
  // override on demand.
  {
    selector: 'node[borderWidth]',
    style: { 'border-width': 'data(borderWidth)' },
  },
  // Phase 9 — Sparse-graph styling via data buckets. The transform writes
  // `_sparseBucket: 'small' | 'medium' | 'large'` per node based on degree,
  // and a graph-level core flag `_sparseGraph` activates the sparse appearance
  // (compact label + smaller node + low opacity). Replaces per-element inline
  // style writes inside `_applyWorkerPositions`.
  {
    selector: 'node[?_sparseBucket][?_sparseShortLabel]',
    style: {
      label: 'data(_sparseShortLabel)',
      'text-wrap': 'none',
      'text-max-width': 22,
      'min-zoomed-font-size': 0,
    },
  },
  {
    selector: 'node[_sparseBucket = "small"]',
    style: {
      width: 8, height: 8,
      'font-size': 3.8,
      'text-opacity': 0.55,
      'background-opacity': 0.55,
    },
  },
  {
    selector: 'node[_sparseBucket = "medium"]',
    style: {
      width: 11, height: 11,
      'font-size': 4.4,
      'text-opacity': 0.65,
      'background-opacity': 0.7,
    },
  },
  {
    selector: 'node[_sparseBucket = "large"]',
    style: {
      width: 13, height: 13,
      'font-size': 4.9,
      'text-opacity': 0.8,
      'background-opacity': 0.85,
    },
  },
  {
    selector: 'edge[?_sparseGraph]',
    style: { width: 0.6, 'line-opacity': 0.12, opacity: 0.12 },
  },
];

const suggestedRules = [
  {
    selector: 'edge[?isSuggested]',
    style: { 'line-style': 'dashed', 'line-dash-pattern': [6, 3], opacity: 0.7 },
  },
  {
    selector: 'node[?isSuggested]',
    style: { 'border-style': 'dashed', opacity: 0.7 },
  },
];

const interactionRules = [
  {
    selector: 'node.hovered',
    style: {
      'border-width': 3,
      'border-color': '#0074D9',
      'underlay-opacity': 0.1,
      'underlay-color': '#0074D9',
      'underlay-padding': 8,
    },
  },
  {
    selector: 'edge.hovered',
    style: {
      width: 2.5,
      'overlay-opacity': 0.08,
      'overlay-color': '#0074D9',
    },
  },
  // S8 — Edge hover label (text wrap + autorotate + text-background) is the
  // dominant cost during hover sweeps. Apply only when explicitly enabled by
  // the chart (gated above by `cy.elements().length <= 300`).
  {
    selector: 'edge.hovered.hovered-edge-label',
    style: {
      label: 'data(connectionType)',
      'font-size': 8,
      color: '#6d6d6d',
      'text-rotation': 'autorotate',
      'text-background-color': '#ffffff',
      'text-background-opacity': 0.85,
      'text-background-padding': '2px',
    },
  },
  {
    selector: 'node:selected',
    style: {
      'border-width': 4,
      'border-color': '#0074D9',
      'underlay-opacity': 0.15,
      'underlay-color': '#0074D9',
      'underlay-padding': 10,
      'overlay-opacity': 0,
    },
  },
  {
    selector: 'edge:selected',
    style: { width: 3, 'line-color': '#0074D9', 'overlay-opacity': 0.1, 'z-index': 10 },
  },
  {
    selector: '.dimmed',
    style: { opacity: 0.25 },
  },
  // S2 — Selection dimming. On select, every element outside the selected
  // node's closed neighborhood gets `data._dim = true`; on unselect we strip
  // the flag. One batched data write replaces the previous per-element
  // `addClass('dimmed')` spam (which fired a style recalc per element).
  {
    selector: 'node[?_dim], edge[?_dim]',
    style: { opacity: 0.25 },
  },
];

const comboRules = [
  {
    selector: ':parent',
    style: {
      'background-opacity': 0.15,
      'border-width': comboStyles.borderWidth,
      'border-style': comboStyles.borderStyle,
      label: 'data(label)',
      'text-valign': 'top',
      'text-halign': 'center',
      'font-size': comboStyles.labelFontSize,
      color: comboStyles.labelColor,
      'text-background-color': comboStyles.labelBackground,
      'text-background-opacity': 1,
      'text-background-padding': '4px',
      padding: '20px',
    },
  },
  ...Object.entries(affiliationComboColors).map(([key, val]) => ({
    selector: `:parent[affiliation="${key}"]`,
    style: {
      'background-color': val.border,
      'background-opacity': 0.15,
      'border-color': val.border,
    },
  })),
  {
    selector: '.highlighted',
    style: {
      'underlay-opacity': 0.4,
      'underlay-color': '#FFD700',
      'underlay-padding': 12,
      'border-width': 3,
      'border-color': '#FFD700',
    },
  },
];

// ── Edge rules ──────────────────────────────────────────────────────
const edgeRules = [
  {
    selector: 'edge',
    style: {
      'curve-style': 'bezier',
      'target-arrow-shape': 'none',
      'line-color': 'data(lineColor)',
      'target-arrow-color': 'data(lineColor)',
      width: 1.5,
    },
  },
  {
    selector: 'edge[connectionType *= "filter_match"]',
    style: { width: 2.5 },
  },
];

// ── Compose final stylesheet ────────────────────────────────────────

export const baseStylesheet = [
  ...nodeRules,
  ...suggestedRules,
  ...edgeRules,
  ...interactionRules,
  ...comboRules,
];

export default baseStylesheet;
